import { BodyText } from "@/components/ui/Text";
import { cn } from "@/lib/utils";
import { formatDate } from "date-fns";
import type {
  ChatParticipant,
  ResourceUniversalChatMessage,
} from "../types/universalChatTypes";
import { MarkdownMessage } from "./MarkdownMessage";
import { TimestampWithStatus } from "./TimestampWithStatus";
import { mapResourceTypeToLabel } from "./messageUtils";

interface ResourceMessageProps {
  message: ResourceUniversalChatMessage;
  includeAvatar?: boolean;
  participant?: ChatParticipant;
}

export const ResourceMessage = ({ message }: ResourceMessageProps) => {
  const senderType = message.senderType;
  const isPatient = senderType === "patient";
  const isSystem = senderType === "system";
  const isClinician = senderType === "clinician";
  const isMarkdown = message.body.markdown;
  const content = message.body.text || "";

  // Get heading from resource type
  const heading = mapResourceTypeToLabel(message.body.resourceType);

  // System messages are centered, others are aligned by sender type
  const alignment = isSystem
    ? "justify-center"
    : isPatient
      ? "justify-start"
      : "justify-end";
  const timestampAlignment = isSystem
    ? "text-center"
    : isPatient
      ? "text-left"
      : "text-right";
  const messageAlign = isSystem ? "center" : isPatient ? "left" : "right";

  return (
    <div className="flex flex-col">
      {/* Message content */}
      <div className={cn("flex mt-2", alignment)}>
        <div className="flex flex-col gap-1 max-w-lg w-full">
          {isMarkdown && content ? (
            <MarkdownMessage
              content={content}
              heading={heading}
              align={messageAlign}
            />
          ) : (
            <div className="p-3 rounded-lg bg-success-subtle">
              <BodyText size="sm" bold>
                Resource Message
              </BodyText>
            </div>
          )}
        </div>
      </div>
      {/* Timestamp */}
      {isClinician ? (
        <TimestampWithStatus
          timestamp={message.createdAt}
          status={message.processingStatus ?? null}
          alignmentClass={timestampAlignment}
        />
      ) : (
        <div className={cn(timestampAlignment)}>
          <BodyText size="xs" textColor="tertiary">
            {formatDate(new Date(message.createdAt), "MM/dd/yyyy h:mm a")}
          </BodyText>
        </div>
      )}
    </div>
  );
};
